import { Dispatch, MutableRefObject, SetStateAction } from 'react'
import { Chart } from 'chart.js'
import { z } from 'zod'

import { ClientSaleReportFormatted } from './format'

import { Pagination } from 'app/types/app/request'
import { Product } from 'app/types/catalogue/product'
import { Supplier } from 'app/types/catalogue/supplier'
import { Machine } from 'app/types/machines/machine'
import { Client } from 'app/types/park/client'
import { Manager } from 'app/types/park/manager'
import { Site } from 'app/types/park/site'
import { StatusReport } from 'app/types/reports'

/** Choice to group the sale report. */
export const GroupBySaleReport = z.enum([
    'PRODUCT',
    'SITE',
    'SUPPLIER',
    'TERMINAL',
])

export type GroupBySaleReport = z.infer<typeof GroupBySaleReport>

/** Choice to sort the sale report. */
export const SortBySaleReport = z.enum([
    'PURCHASE_PRICE_ASC',
    'PURCHASE_PRICE_DESC',
    'QUANTITY_ASC',
    'QUANTITY_DESC',
    'TURNOVER_ASC',
    'TURNOVER_DESC',
])

export type SortBySaleReport = z.infer<typeof SortBySaleReport>

export const Totals = z.object({
    quantity: z.number().nonnegative(),
    total_purchase_price: z.number().nonnegative(),
    turnover_without_vat: z.number().nonnegative(),
})

/** The product with data added for the sale report. */
export const ProductSaleReport = Product.pick({
    id: true,
    name: true,
    ean: true,
}).merge(Totals)

export type ProductSaleReport = z.infer<typeof ProductSaleReport>

/** The supplier with data added for the sale report. */
export const SupplierSaleReport = Supplier.pick({
    id: true,
    name: true,
}).extend({
    /** The list of products sold by the supplier. */
    products: ProductSaleReport.array().min(1),
})

/** The terminal with data added for the sale report. */
export const TerminalSaleReport = Machine.pick({
    id: true,
    name: true,
}).extend({
    /** The list of suppliers of the machine. */
    suppliers: SupplierSaleReport.array().min(1),
})

/** The site with data added for the sale report. */
export const SiteSaleReport = Site.pick({
    id: true,
    name: true,
}).extend({
    /** The list of terminals of the site. */
    terminals: TerminalSaleReport.array().min(1),
})

/** The client with data added for the sale report. */
export const ClientSaleReport = Client.pick({
    id: true,
    name: true,
}).extend({
    /** The list of sites of the client. */
    sites: SiteSaleReport.array().min(1),
})

/** The sale report with data. */
export const SaleReport = z.object({
    /** The ID of the sale report. */
    id: z.number().positive(),

    /** The creation datetime of the sale report. */
    creation_datetime: z.string().datetime({ offset: true }),

    /** The start date of the sale report interval. */
    date_start: z.string().date(),

    /** The start end of the sale report interval. */
    date_end: z.string().date(),

    /** Status. */
    status: StatusReport,

    /** The supplier filter used in the creation. */
    filter_supplier: Supplier.pick({
        id: true,
        name: true,
    }).array(),

    /** The terminal filter used in the creation. */
    filter_terminal: Machine.pick({
        id: true,
        name: true,
    }).array(),

    /** The site filter used in the creation. */
    filter_site: Site.pick({
        id: true,
        name: true,
    }).array(),

    /** The client filter used in the creation. */
    filter_client: Client.pick({
        id: true,
        name: true,
    }).array(),

    /** The manager with data added. */
    manager: Manager.pick({
        id: true,
        name: true,
    })
        .merge(Totals)
        .extend({
            /** The list of clients. */
            clients: ClientSaleReport.array(),
        }),
})

export type SaleReport = z.infer<typeof SaleReport>

/** The data sent to create a sale report. */
export const SaleReportPayload = SaleReport.pick({
    date_start: true,
    date_end: true,
})
    .extend({
        /** The IDs of suppliers. */
        suppliers: Supplier.shape.id.array().optional(),
    })
    .and(
        z.union([
            z.object({
                terminals: Machine.shape.id.array().min(1),
            }),
            z.object({
                sites: Site.shape.id.array().min(1),
            }),
            z.object({
                clients: Client.shape.id.array().min(1),
            }),

            z.object({
                /** The ID of the manager. */
                manager: Manager.shape.id.optional(),
            }),
        ]),
    )

export type SaleReportPayload = z.infer<typeof SaleReportPayload>

/** The supplier with data added for the sale report. */
export const SupplierSaleReportInitial = SupplierSaleReport.merge(
    Totals,
).extend({
    /** The list of products sold by the supplier. */
    products: ProductSaleReport.array().min(1),
})

/** The terminal with data added for the sale report. */
export const TerminalSaleReportInitial = TerminalSaleReport.merge(
    Totals,
).extend({
    /** The list of suppliers of the machine. */
    suppliers: SupplierSaleReportInitial.array().min(1),
})

/** The site with data added for the sale report. */
export const SiteSaleReportInitial = SiteSaleReport.merge(Totals).extend({
    /** The list of machines of the site. */
    terminals: TerminalSaleReportInitial.array().min(1),
})

/** The client with data added for the sale report. */
export const ClientSaleReportInitial = ClientSaleReport.merge(Totals).extend({
    /** The list of sites of the client. */
    sites: SiteSaleReportInitial.array().min(1),
})

/** The sale report with data. */
export const SaleReportInitial = SaleReport.extend({
    /** The manager with data added. */
    manager: Manager.pick({
        id: true,
        name: true,
    })
        .merge(Totals)
        .extend({
            /** The list of clients. */
            clients: ClientSaleReportInitial.array().min(1),
        }),
})

/** All sale reports returned by the API. */
export const SaleReportsRequest = Pagination.extend({
    results: SaleReport.array(),
})

export type SaleReportsRequest = z.infer<typeof SaleReportsRequest>

export type ChartReference = {
    client: ClientSaleReportFormatted
    reference: MutableRefObject<Chart<'bar'> | null>
}

export type ChartReferencesSetter = Dispatch<SetStateAction<ChartReference[]>>
